import { useRef, useState } from "react"
import { FieldErrors } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useOutletContext } from "react-router"
import { LoadingSpinner } from "@/components/loading-spinner"
import { TranslatedFieldError } from "@/components/ui/form-field-error"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SearchContent, SearchSelectInput, SuggestionSearch } from "@/components/ui/search"
import { useClickOutside } from "@/hooks/useClickOutside"
import { HYPHEN_CHARACTER } from "@/lib/format-date"
import { IcdCodeSeparator } from "@/modules/patients/detail/episode-of-care/icd-code-separator"
import { useIcdCodeByDiagnosisCode } from "@/modules/patients/detail/progress-note/hooks/useIcdCodeByDiagnosisCode"
import { useFetchIcdCodesQuery } from "@/modules/patients/hooks/useIcdDiagnosisQuery"
import { IcdCodeSearchOption } from "@/modules/patients/libs/icd-code-search-option"
import { IcdDiagnosisFragment$data } from "@/modules/patients/services/__generated__/IcdDiagnosisFragment.graphql"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { deEpisodeOfCareFragment$data } from "@/modules/patients/services/de/__generated__/deEpisodeOfCareFragment.graphql"

export const MIN_DIAGNOSIS_CODE_SEARCH_CHARACTERS = 3

export const formatDiagnosisCodeText = (code: string = "", description: string | null = ""): string => {
  if (code && description?.trim()) {
    return `${code} ${HYPHEN_CHARACTER} ${description.trim()}`
  }

  return (code || description?.trim()) ?? ""
}

interface DiagnosisCodeProps extends FetchKeyProps {
  name: string
  errors: FieldErrors
  invalid: boolean
  value: deEpisodeOfCareFragment$data["icdDiagnosis"]["diagnosisCode"]
  defaultValue: deEpisodeOfCareFragment$data["icdDiagnosis"]["diagnosisCode"] | undefined
  onSelect: (diagnosisCode: string) => void
}

export const DiagnosisCode = (props: DiagnosisCodeProps) => {
  const { name, errors, invalid, value, defaultValue, fetchKey, onSelect } = props
  const { t } = useTranslation()
  const { code, description } = useIcdCodeByDiagnosisCode({
    diagnosisCode: value,
    skip: value !== defaultValue || value.trim() === "",
    fetchKey,
  })
  const { patient } = useOutletContext<{ patient: PatientFragment$data }>()

  const defaultTextInput = formatDiagnosisCodeText(code, description)
  const [searchValue, setSearchValue] = useState(defaultTextInput)
  const [searchResultVisible, setSearchResultVisible] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)
  const searchResultRef = useRef<HTMLDivElement>(null)

  const { icdCodes, debounceSearch, isLoading } = useFetchIcdCodesQuery({
    birthDay: patient.birthDate,
    gender: patient.gender,
    searchType: "EPISODE_OF_CARE",
  })

  const handleChange = (value: string) => {
    if (value.length === 0) onSelect("")
    debounceSearch(value)
    setSearchValue(value)
  }

  const handleDiagnosisCodeSelection = (option: IcdDiagnosisFragment$data) => {
    if (!option.diagnosisCode) return
    const value = formatDiagnosisCodeText(option.diagnosisCode, option.text)

    setSearchValue(value)
    onSelect(option.diagnosisCode)
    setSearchResultVisible(false)
  }

  const handleClearInput = () => {
    debounceSearch("")
    setSearchValue("")
    onSelect("")
    setSearchResultVisible(true)
  }

  useClickOutside((event: MouseEvent) => {
    if (searchResultRef.current && !searchResultRef.current.contains(event.target as HTMLElement)) {
      setSearchResultVisible(false)
    }
  }, searchRef)

  return (
    <SuggestionSearch>
      <SearchSelectInput
        testId="diagnosis-code"
        ref={searchRef}
        label={t("patient.therapyPrescription.diagnosisCode")}
        value={searchValue}
        error={invalid}
        required
        onChange={({ target: { value } }) => {
          handleChange(value)
        }}
        onFocus={() => setSearchResultVisible(true)}
        onClear={handleClearInput}
      />
      <TranslatedFieldError errors={errors} name={name} />
      <SearchContent
        ref={searchResultRef}
        className={searchValue.length >= 3 && searchResultVisible ? "show" : "hidden"}>
        {isLoading && (
          <div className="my-2 flex w-full items-center justify-center">
            <LoadingSpinner size={24} />
          </div>
        )}
        {!isLoading &&
          (!icdCodes || (icdCodes.commons.length === 0 && icdCodes.others.length === 0) ? (
            <div className="text-on-surface-subdued relative cursor-default px-2 text-sm select-none">
              {t("common.nothingFound")}
            </div>
          ) : (
            <ScrollArea>
              <div className="py-2">
                {!!icdCodes.commons.length && (
                  <>
                    <div className="text-on-surface-subdued py-2.5 pl-2 text-sm leading-5 font-medium">
                      {t("episodeOfCare.common")}
                    </div>
                    {icdCodes.commons.map((option) => (
                      <IcdCodeSearchOption
                        key={option.entry.diagnosisCode}
                        option={option.entry}
                        highlights={option.highlights}
                        onSelect={handleDiagnosisCodeSelection}
                      />
                    ))}
                  </>
                )}
                {!!icdCodes.others.length && (
                  <>
                    {!!icdCodes.commons.length && <IcdCodeSeparator />}
                    <div className="text-on-surface-subdued py-2.5 pl-2 text-sm leading-5 font-medium">
                      {t("episodeOfCare.other")}
                    </div>
                    {icdCodes.others.map((option) => (
                      <IcdCodeSearchOption
                        key={option.entry.diagnosisCode}
                        option={option.entry}
                        highlights={option.highlights}
                        onSelect={handleDiagnosisCodeSelection}
                      />
                    ))}
                  </>
                )}
              </div>
            </ScrollArea>
          ))}
      </SearchContent>
    </SuggestionSearch>
  )
}
